"use client";

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { prodServerUrl } from "@/app/utils/globals";
import Link from "next/link";
import { 
  FaCalendarAlt, 
  FaClock, 
  FaVideo, 
  FaUserTie, 
  FaTrophy, 
  FaLightbulb, 
  FaGamepad, 
  FaPlayCircle, 
  FaCheckCircle 
} from "react-icons/fa";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { motion, AnimatePresence } from "framer-motion";

// Register ScrollTrigger
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

// Tech words for the puzzle game
const techWords = [
  { word: "PYTHON", hint: "Popular programming language used in data science", question: "What programming language is known for its use in data science and has a snake-related name?" },
  { word: "REACT", hint: "JavaScript library for building user interfaces", question: "Which popular JavaScript library developed by Facebook is used for building interactive UI components?" },
  { word: "MACHINE", hint: "_____ Learning is a subset of AI", question: "Fill in the blank: '_____ Learning' is a subset of artificial intelligence that uses data to improve over time." },
  { word: "ETHICAL", hint: "_____ Hacking is about finding vulnerabilities to fix them", question: "Fill in the blank: '_____ Hacking' refers to authorized attempts to gain access to systems to identify security vulnerabilities." },
  { word: "DATA", hint: "_____ Science involves analyzing large datasets", question: "Fill in the blank: '_____ Science' is the field that uses scientific methods to extract knowledge from structured and unstructured data." },
];

const EventSection = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const sectionRef = useRef(null);
  
  // Game States
  const [gameStarted, setGameStarted] = useState(false);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [userGuess, setUserGuess] = useState("");
  const [score, setScore] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [feedback, setFeedback] = useState(null); // 'correct' | 'wrong'

  // Fetch Events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const { data } = await axios.get(`${prodServerUrl}/key-events`);
        if (data.success) {
          setEvents(data.keyEvents || []);
        }
      } catch (err) {
        console.error("Failed to fetch events", err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  // GSAP Animations
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".event-card",
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.2,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 70%",
          },
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, [events]);

  // Game Logic
  const handleGuess = (e) => {
    e.preventDefault();
    const targetWord = techWords[currentWordIndex].word;
    
    if (userGuess.toUpperCase() === targetWord) {
      setFeedback("correct");
      setScore(prev => prev + 10);
      setTimeout(() => {
        setFeedback(null);
        setUserGuess("");
        setShowHint(false);
        if (currentWordIndex < techWords.length - 1) {
          setCurrentWordIndex(prev => prev + 1);
        } else {
          // Game Over logic could go here
          setCurrentWordIndex(0); // Loop for now
        }
      }, 1500);
    } else {
      setFeedback("wrong");
      setTimeout(() => setFeedback(null), 1000);
    }
  };

  return (
    <section ref={sectionRef} className="relative py-24 bg-slate-950 overflow-hidden font-sans">
      
      {/* --- Background Effects --- */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[100px]"></div>
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block py-1 px-3 rounded-full bg-blue-500/10 text-blue-400 text-xs font-bold uppercase tracking-wide border border-blue-500/20 mb-4">
            Live Sessions
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Upcoming <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Masterclasses</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-lg">
            Join industry experts for live interactive sessions. Learn cutting-edge skills and get your doubts clarified instantly.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : events.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {events.map((event, idx) => (
              <div key={idx} className="event-card group relative bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden hover:border-blue-500/50 transition-all duration-300 hover:-translate-y-2">
                {/* Glow Effect on Hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                
                <div className="p-6 relative z-10 h-full flex flex-col">
                  {/* Date Badge */}
                  <div className="flex justify-between items-start mb-6">
                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-3 text-center min-w-[70px]">
                      <span className="block text-xs text-slate-400 uppercase font-bold">{event.dayOfWeek?.slice(0,3)}</span>
                      <span className="block text-2xl font-bold text-white">{event.date?.split(' ')[1]?.replace(',','')}</span>
                    </div>
                    <div className="bg-green-500/10 text-green-400 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span> Live
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">{event.title}</h3>
                  <p className="text-slate-400 text-sm mb-6 line-clamp-2 flex-grow">{event.subtitle}</p>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3 text-sm text-slate-300">
                      <FaClock className="text-blue-500" /> {event.time}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-300">
                      <FaUserTie className="text-purple-500" /> {event.speaker}
                    </div>
                  </div>

                  <a 
                    href={event.joinLink} 
                    target="_blank" 
                    rel="noreferrer"
                    className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-colors shadow-lg shadow-blue-900/20"
                  >
                    <FaVideo /> Join Session
                  </a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* --- Empty State / Game UI --- */
          <div className="max-w-4xl mx-auto bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
            
            <div className="mb-10">
              <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl">🗓️</div>
              <h3 className="text-2xl font-bold text-white mb-2">No Upcoming Events</h3>
              <p className="text-slate-400">There are no live sessions scheduled right now. Check back later!</p>
              
              <Link href="/events" className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-full font-medium transition-all">
                <FaPlayCircle /> Watch Past Recordings
              </Link>
            </div>

            {/* --- Mini Game Widget --- */}
            <div className="mt-12 pt-12 border-t border-slate-800">
              <div className="flex items-center justify-center gap-2 mb-6">
                <FaGamepad className="text-purple-500 text-xl" />
                <span className="text-sm font-bold text-purple-400 uppercase tracking-widest">Tech Brain Teaser</span>
              </div>

              {!gameStarted ? (
                <div className="bg-slate-800/50 rounded-2xl p-8 max-w-lg mx-auto border border-slate-700">
                  <h4 className="text-xl font-bold text-white mb-3">Test Your Tech Knowledge</h4>
                  <p className="text-slate-400 text-sm mb-6">Solve riddles, guess the tech terms, and sharpen your mind while you wait.</p>
                  <button 
                    onClick={() => setGameStarted(true)}
                    className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold rounded-xl hover:shadow-lg hover:scale-105 transition-all"
                  >
                    Start Challenge
                  </button>
                </div>
              ) : (
                <div className="max-w-md mx-auto">
                  <div className="flex justify-between items-center mb-6 px-4">
                    <span className="text-slate-400 text-sm">Question {currentWordIndex + 1}/{techWords.length}</span>
                    <div className="flex items-center gap-2 bg-slate-800 px-3 py-1 rounded-full">
                      <FaTrophy className="text-yellow-400" />
                      <span className="text-white font-bold">{score}</span>
                    </div>
                  </div>

                  <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 mb-6 relative overflow-hidden">
                    <AnimatePresence mode="wait">
                      {feedback === "correct" && (
                        <motion.div 
                          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                          className="absolute inset-0 bg-green-500/90 z-20 flex items-center justify-center flex-col text-white"
                        >
                          <FaCheckCircle className="text-5xl mb-2" />
                          <span className="font-bold text-xl">Correct! +10</span>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <p className="text-lg text-white font-medium mb-6 leading-relaxed">
                      {techWords[currentWordIndex].question}
                    </p>

                    <form onSubmit={handleGuess} className="relative">
                      <input 
                        type="text" 
                        value={userGuess}
                        onChange={(e) => setUserGuess(e.target.value)}
                        placeholder="Type your answer..."
                        className={`w-full bg-slate-900 border ${feedback === 'wrong' ? 'border-red-500' : 'border-slate-600'} rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors text-center font-mono uppercase tracking-widest`}
                      />
                      {feedback === 'wrong' && <span className="absolute right-4 top-3.5 text-red-500 text-xs font-bold">Try Again</span>}
                    </form>

                    {showHint && (
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 text-center">
                        <span className="text-yellow-400 text-sm bg-yellow-400/10 px-3 py-1 rounded-full border border-yellow-400/20">
                          Hint: {techWords[currentWordIndex].hint}
                        </span>
                      </motion.div>
                    )}
                  </div>

                  <div className="flex justify-center gap-4">
                    <button 
                      onClick={() => setShowHint(true)}
                      disabled={showHint}
                      className="text-slate-400 hover:text-white text-sm flex items-center gap-2 transition-colors"
                    >
                      <FaLightbulb /> Need a hint?
                    </button>
                    <button 
                      onClick={handleGuess}
                      className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-lg font-bold text-sm transition-colors"
                    >
                      Submit
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default EventSection;